import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import "./Chat.scss";

import deliveredIcon from "./img/delivered.png";
import readedIcon from "./img/readed.png";
import clipIcon from "./img/clip.png";
import storageIcon from "./img/storage.png";
import backIcon from "./img/back.png";
import sendIcon from "./img/send.png";

import {
  FILE_ACCEPT,
  MEDIA_ACCEPT,
  applyChanges,
  canReplyTo,
  formatDuration,
  formatFileSize,
  hasReaction,
  kindOfFile,
  lastServerCreatedAt,
  makeClientId,
  mergeMessages,
  patchLocal,
  primaryAttachment,
  readVideoDuration,
  reconcileSent,
  replyBody,
  replyPreviewOf,
  validateUploadFile,
  validateVideoDuration,
  withReaction,
} from "./chatV2";
import ChatActions from "./ChatActions";
import ChatStorage from "./ChatStorage";
import PhotoDetails from "../PhotoDetails/PhotoDetails";

/* helpers */
function useIsMobile(breakpoint = 1023) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    try {
      mql.addEventListener("change", onChange);
    } catch {
      mql.addListener(onChange);
    }
    setIsMobile(mql.matches);
    return () => {
      try {
        mql.removeEventListener("change", onChange);
      } catch {
        mql.removeListener(onChange);
      }
    };
  }, [breakpoint]);
  return isMobile;
}

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";
const EDIT_WINDOW_MS = 15 * 60 * 1000;

const fmtTime = (ts) => {
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts || Date.now());
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
};
const fmtDay = (ts) => {
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts || Date.now());
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
const useQuery = () => new URLSearchParams(useLocation().search);

function useIOSKeyboardOffset({ patchViewport = true } = {}) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const isIOS =
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const root = document.documentElement;

    root.style.webkitTextSizeAdjust = "100%";

    if (isIOS && patchViewport) {
      const meta = document.querySelector('meta[name="viewport"]');
      const target =
        "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no";
      if (meta) {
        const current = meta.getAttribute("content") || "";
        const needMax = !/maximum-scale=1/.test(current);
        const needUsr = !/user-scalable=no/.test(current);
        if (needMax || needUsr) {
          const parts = [current];
          if (needMax) parts.push("maximum-scale=1");
          if (needUsr) parts.push("user-scalable=no");
          meta.setAttribute(
            "content",
            parts.join(", ").replace(/\s+,/g, ",")
          );
        }
        if (!/viewport-fit=cover/.test(meta.getAttribute("content") || "")) {
          meta.setAttribute(
            "content",
            (meta.getAttribute("content") || "") + ", viewport-fit=cover"
          );
        }
      } else {
        const m = document.createElement("meta");
        m.setAttribute("name", "viewport");
        m.setAttribute("content", target);
        document.head.appendChild(m);
      }
    }

    if (window.visualViewport) {
      const vv = window.visualViewport;
      const update = () => {
        const keyboard = Math.max(
          0,
          window.innerHeight - vv.height - (vv.offsetTop || 0)
        );
        root.style.setProperty("--kb", Math.round(keyboard) + "px");
      };
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
      window.addEventListener("orientationchange", update);
      update();
      return () => {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
        window.removeEventListener("orientationchange", update);
      };
    } else {
      root.style.setProperty("--kb", "0px");
    }
  }, [patchViewport]);
}

/* ===== Cvanto link preview helpers ===== */
const previewCache = new Map();

const extractCvantoLink = (text) => {
  const s = String(text || "");
  const re =
    /(https?:\/\/(?:localhost:3000|(?:www\.)?cvanto\.com)\/(post|listing)\/([a-zA-Z0-9-]+))(?:[^\s]*)?/i;
  const m = re.exec(s);
  if (!m) return null;
  const url = m[1];
  const type = m[2]; // "post" | "listing"
  const rawId = m[3] || "";
  const id = rawId.split(/[?#]/)[0];
  return { url, type, id };
};

const LinkPreview = ({ meta }) => {
  const [state, setState] = useState(() => {
    if (previewCache.has(meta.url)) {
      return { loading: false, error: false, data: previewCache.get(meta.url) };
    }
    return { loading: true, error: false, data: null };
  });

  useEffect(() => {
    let cancelled = false;

    if (previewCache.has(meta.url)) {
      setState({
        loading: false,
        error: false,
        data: previewCache.get(meta.url),
      });
      return;
    }

    const load = async () => {
      try {
        const path =
          meta.type === "post"
            ? `/api/posts/${encodeURIComponent(meta.id)}`
            : `/api/listings/${encodeURIComponent(meta.id)}`;

        const res = await fetch(`${API_BASE}${path}`);
        if (!res.ok) {
          if (!cancelled) setState({ loading: false, error: true, data: null });
          return;
        }
        const data = await res.json();

        let image =
          data.cover ||
          data.mainPhoto ||
          (Array.isArray(data.photos) && data.photos[0]) ||
          (Array.isArray(data.images) && data.images[0]) ||
          null;

        if (!image && data.photo) {
          image = data.photo;
        }

        if (image && typeof image === "object") {
          image = image.url || image.src || image.path || null;
        }

        if (!image) {
          if (!cancelled) setState({ loading: false, error: false, data: null });
          return;
        }

        const title =
          data.title ||
          data.headline ||
          data.name ||
          (meta.type === "post"
            ? "Публікація на Cvanto"
            : "Оголошення на Cvanto");

        const description = data.description || data.text || "";
        const price =
          typeof data.price === "number"
            ? `${data.price} ₴`
            : data.price || "";

        const payload = { image, title, description, price };
        previewCache.set(meta.url, payload);

        if (!cancelled)
          setState({ loading: false, error: false, data: payload });
      } catch {
        if (!cancelled) setState({ loading: false, error: true, data: null });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [meta.url, meta.type, meta.id]);

  if (state.loading || state.error || !state.data) return null;

  const { image, title, description, price } = state.data;

  return (
    <a
      className="link-preview"
      href={meta.url}
      target="_blank"
      rel="noreferrer"
    >
      <div className="link-preview__header">
        <span className="link-preview__domain">cvanto.com</span>
        {price && <span className="link-preview__price">{price}</span>}
      </div>
      <div className="link-preview__body">
        <div className="link-preview__text">
          <div className="link-preview__title">{title}</div>
          {description && (
            <div className="link-preview__desc">{description}</div>
          )}
        </div>
        <div className="link-preview__thumb-wrap">
          <img src={image} alt="" className="link-preview__thumb" />
        </div>
      </div>
    </a>
  );
};

/* component */
const Chat = ({
  threadId: threadIdProp = null,
  peerId: peerIdProp = null,
  onBack,
  onConversationCreated,
  disabled = false,
}) => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(1023);
  const query = useQuery();

  useIOSKeyboardOffset({ patchViewport: true });

  const [threadId, setThreadId] = useState(threadIdProp || null);
  const [peerId, setPeerId] = useState(peerIdProp || query.get("to") || null);

  const prefillParam = query.get("prefill") || "";

  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const [text, setText] = useState("");
  const [isPeerTyping, setIsPeerTyping] = useState(false);
  const [error, setError] = useState("");
  const [peerLastReadAt, setPeerLastReadAt] = useState(0);

  const [editing, setEditing] = useState(null);
  const [ctx, setCtx] = useState({ open: false, x: 0, y: 0, msg: null });
  const [viewerSrc, setViewerSrc] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [storageOpen, setStorageOpen] = useState(false);

  const [showScrollDown, setShowScrollDown] = useState(false);

  // Chat V2
  const [replyTarget, setReplyTarget] = useState(null);
  const [pending, setPending] = useState([]);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const mediaInputRef = useRef(null);
  const docInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const changedAtRef = useRef(0);
  const messagesRef = useRef([]);

  const listRef = useRef(null);
  const endRef = useRef(null);
  const typingTimer = useRef(null);
  const abortRef = useRef(null);

  const nearBottomRef = useRef(true);
  const initScrollDoneRef = useRef(false);
  const lastChatKeyRef = useRef("");

  const scrollAnimRef = useRef(null);

  const composerRef = useRef(null);
  const textareaRef = useRef(null);
  const [composerH, setComposerH] = useState(64);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const cancelScrollAnim = useCallback(() => {
    if (scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
  }, []);

  useEffect(() => {
    setComposerH(isMobile ? 62 : 64);
  }, [isMobile]);

  const syncComposerHeight = useCallback(() => {
    const ta = textareaRef.current;
    const comp = composerRef.current;
    if (!ta || !comp) return;

    const maxTa = isMobile ? 112 : 132;

    ta.style.height = "auto";
    const next = Math.min(ta.scrollHeight, maxTa);
    ta.style.height = next + "px";
    ta.style.overflowY = ta.scrollHeight > maxTa ? "auto" : "hidden";

    requestAnimationFrame(() => {
      const h = Math.ceil(comp.getBoundingClientRect().height || 0);
      const base = isMobile ? 62 : 64;
      const cap = isMobile ? 142 : 164;
      const finalH = Math.max(base, Math.min(cap, h || base));
      setComposerH(finalH);
    });
  }, [isMobile]);

  useEffect(() => {
    syncComposerHeight();
  }, [text, editing, disabled, syncComposerHeight]);

  useEffect(() => {
    const onResize = () => syncComposerHeight();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [syncComposerHeight]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {}
  }, []);

  const apiFetch = useCallback(
  async (url, options = {}) => {
    const fullUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `${API_BASE}${url}`;

    const token = await getAccessToken();

    return fetch(fullUrl, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  },
  []
);

  const loadPeerProfile = useCallback(
    async (id) => {
      if (!id) return;
      try {
        const r = await apiFetch(`/api/account/${encodeURIComponent(id)}`);
        if (!r.ok) {
          setPeer(
            (prev) =>
              prev || { id, username: "Користувач", avatar: DEFAULT_AVATAR }
          );
          return;
        }
        const u = await r.json();
        setPeer({
          id,
          username: u.username || "Користувач",
          avatar: u.avatar || DEFAULT_AVATAR,
        });
      } catch {
        setPeer(
          (prev) =>
            prev || { id, username: "Користувач", avatar: DEFAULT_AVATAR }
        );
      }
    },
    [apiFetch]
  );

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setError("");

    if (threadId) {
      try {
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;

        const res = await apiFetch(
  `/api/messages/chat/thread?threadId=${encodeURIComponent(threadId)}`,
  { signal: ac.signal }
);

        if (res.status === 404) {
          setMessages([]);
          setHasMore(false);
          setIsPeerTyping(false);
          setPeerLastReadAt(0);
          setPeer(null);
          setError("Чат не знайдено (можливо видалено).");
          return;
        }

        if (!res.ok) throw new Error("Не вдалося завантажити чат");
        const data = await res.json();

        setMessages(Array.isArray(data.messages) ? data.messages : []);
        changedAtRef.current = Number(data.changedAt || 0);
        setHasMore(!!data.hasMore);
        setIsPeerTyping(!!data.isPeerTyping);
        setPeerLastReadAt(Number(data.peerLastReadAt || 0));

        if (data.peer?.id) {
          setPeer({ id: data.peer.id });
          setPeerId((prev) => prev || data.peer.id);
          loadPeerProfile(data.peer.id);
        } else if (peerId) {
          setPeer({ id: peerId });
          loadPeerProfile(peerId);
        } else {
          setPeer(null);
        }
      } catch (e) {
        if (e?.name !== "AbortError")
          setError(e.message || "Помилка завантаження");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (peerId) {
      try {
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;

        const res = await apiFetch(
  `/api/messages/chat/thread-by-peer?peerId=${encodeURIComponent(peerId)}`,
  { signal: ac.signal }
);

        if (res.status === 404) {
          setMessages([]);
          setHasMore(false);
          setIsPeerTyping(false);
          setPeerLastReadAt(0);
          setPeer({ id: peerId });
          loadPeerProfile(peerId);
          return;
        }

        if (!res.ok) throw new Error("Не вдалося завантажити чат");
        const data = await res.json();

        if (data.exists) {
          setThreadId(data.threadId);
          setPeer({ id: data.peer?.id || peerId });
          setMessages(Array.isArray(data.messages) ? data.messages : []);
        changedAtRef.current = Number(data.changedAt || 0);
          setHasMore(!!data.hasMore);
          setIsPeerTyping(!!data.isPeerTyping);
          setPeerLastReadAt(Number(data.peerLastReadAt || 0));
          loadPeerProfile(data.peer?.id || peerId);
        } else {
          setMessages([]);
          setHasMore(false);
          setPeer({ id: peerId });
          loadPeerProfile(peerId);
        }
      } catch (e) {
        if (e?.name !== "AbortError")
          setError(e.message || "Помилка завантаження");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(false);
    setError("Немає даних для відкриття чату.");
  }, [threadId, peerId, userId, apiFetch, loadPeerProfile]);

  useEffect(() => {
    bootstrap();
  }, [threadId, peerId, bootstrap]);

  useEffect(() => {
    if (prefillParam && !text.trim()) setText(prefillParam);
  }, [prefillParam, text]);

  const animateScrollToBottom = useCallback(
    (duration = 260) => {
      const el = listRef.current;
      if (!el) return;

      cancelScrollAnim();

      const startTop = el.scrollTop;
      const start = performance.now();
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const step = (now) => {
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        const to = maxTop;
        const diff = to - startTop;

        const t = Math.min(1, (now - start) / duration);
        const p = easeOutCubic(t);

        el.scrollTop = startTop + diff * p;

        if (t < 1) {
          scrollAnimRef.current = requestAnimationFrame(step);
        } else {
          el.scrollTop = to;
          scrollAnimRef.current = null;
        }
      };

      scrollAnimRef.current = requestAnimationFrame(step);
    },
    [cancelScrollAnim]
  );

  const scrollToEnd = useCallback(
    (smooth = false) => {
      const el = listRef.current;
      if (!el) return;

      if (smooth) {
        animateScrollToBottom(260);
        return;
      }

      cancelScrollAnim();
      el.scrollTop = el.scrollHeight;
      if (endRef.current) {
        endRef.current.scrollIntoView({ behavior: "auto", block: "end" });
      }
      el.scrollTop = el.scrollHeight;
    },
    [animateScrollToBottom, cancelScrollAnim]
  );

  const syncScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = dist < 48;
    nearBottomRef.current = atBottom;
    setShowScrollDown(!atBottom);
  }, []);

  useEffect(() => {
    syncScrollState();
  }, [syncScrollState, loading]);

  useEffect(() => {
    const key = threadId ? `t:${threadId}` : peerId ? `p:${peerId}` : "";
    if (key !== lastChatKeyRef.current) {
      lastChatKeyRef.current = key;
      initScrollDoneRef.current = false;
      nearBottomRef.current = true;
      setShowScrollDown(false);
      cancelScrollAnim();
    }
  }, [threadId, peerId, cancelScrollAnim]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onUserIntent = () => cancelScrollAnim();

    el.addEventListener("touchstart", onUserIntent, { passive: true });
    el.addEventListener("wheel", onUserIntent, { passive: true });
    el.addEventListener("mousedown", onUserIntent, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onUserIntent);
      el.removeEventListener("wheel", onUserIntent);
      el.removeEventListener("mousedown", onUserIntent);
    };
  }, [cancelScrollAnim]);

  useEffect(() => {
    if (loading) return;
    if (!listRef.current) return;

    if (!initScrollDoneRef.current) {
      initScrollDoneRef.current = true;

      requestAnimationFrame(() => {
        scrollToEnd(false);
        syncScrollState();
        setTimeout(() => {
          scrollToEnd(false);
          syncScrollState();
        }, 60);
        setTimeout(() => {
          scrollToEnd(false);
          syncScrollState();
        }, 220);
      });

      return;
    }

    if (nearBottomRef.current) {
      requestAnimationFrame(() => {
        scrollToEnd(false);
        syncScrollState();
        setTimeout(() => {
          scrollToEnd(false);
          syncScrollState();
        }, 60);
      });
    } else {
      syncScrollState();
    }
  }, [messages.length, loading, scrollToEnd, syncScrollState]);

  const handleImageLoad = useCallback(() => {
    if (!nearBottomRef.current) return;
    requestAnimationFrame(() => {
      scrollToEnd(false);
      syncScrollState();
    });
  }, [scrollToEnd, syncScrollState]);

  const sendReadReceipts = useCallback(async () => {
    if (!threadId) return;
    try {
      await apiFetch(`/api/messages/chat/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ threadId }),
      });
    } catch {}
  }, [threadId, apiFetch]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 16;
      if (nearBottom) sendReadReceipts();
      syncScrollState();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    sendReadReceipts();
    syncScrollState();
    return () => el.removeEventListener("scroll", onScroll);
  }, [sendReadReceipts, syncScrollState]);

  useEffect(() => {
    if (!threadId) return;
    let busy = false;
    const tick = async () => {
      if (busy) return;
      busy = true;
      try {
        const since = lastServerCreatedAt(messagesRef.current);
        const res = await apiFetch(
          `/api/messages/chat/thread?threadId=${encodeURIComponent(threadId)}${
            since ? `&since=${encodeURIComponent(since)}` : ""
          }&changedSince=${encodeURIComponent(changedAtRef.current)}`
        );

        if (!res.ok) return;
        const data = await res.json();
        const incoming = Array.isArray(data.messages) ? data.messages : [];
        const changes = Array.isArray(data.changes) ? data.changes : [];
        if (incoming.length || changes.length) {
          setMessages((prev) => applyChanges(mergeMessages(prev, incoming), changes));
        }
        if (typeof data.changedAt === "number") changedAtRef.current = data.changedAt;
        if (typeof data.isPeerTyping === "boolean") setIsPeerTyping(data.isPeerTyping);
        if (typeof data.peerLastReadAt === "number") setPeerLastReadAt(data.peerLastReadAt);
      } catch {
        /* retried on the next tick */
      } finally {
        busy = false;
      }
    };
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") tick();
    }, 4000);
    return () => clearInterval(iv);
  }, [threadId, apiFetch]);

  const announceTyping = useCallback(
    async (flag) => {
      if (!peerId && !threadId) return;
      try {
        await apiFetch(`/api/messages/chat/typing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            threadId: threadId || undefined,
            to: !threadId ? peerId : undefined,
            isTyping: flag,
          }),
        });
      } catch {}
    },
    [peerId, threadId, apiFetch]
  );

  const onChangeText = (e) => {
    setText(e.target.value);
    if (disabled) return;
    clearTimeout(typingTimer.current);
    announceTyping(true);
    typingTimer.current = setTimeout(() => announceTyping(false), 1200);
  };

  const openMenuAt = (x, y, msg) => setCtx({ open: true, x, y, msg });
  const closeMenu = () => setCtx({ open: false, x: 0, y: 0, msg: null });

  const startEdit = (msg) => {
    setEditing({
      createdAt: msg.createdAt,
      id: msg.id,
      originalText: msg.text,
    });
    setText(msg.text);
    closeMenu();
  };
  const cancelEdit = () => {
    setEditing(null);
    setText("");
  };

  const doUnsend = async (msg) => {
    closeMenu();
    if (!threadId) return;
    try {
      const res = await apiFetch(`/api/messages/chat/unsend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ threadId, createdAt: msg.createdAt }),
      });
      if (!res.ok) throw new Error("Не вдалося скасувати");
      await res.json();
      setMessages((prev) =>
        prev.map((m) =>
          m.createdAt === msg.createdAt
            ? { ...m, type: "unsent", text: "", attachments: [], reactions: [], replyTo: undefined }
            : m
        )
      );
    } catch (e) {
      setError(e.message || "Помилка скасування");
    }
  };

  // Reactions: only ever ADD from the menu or REMOVE my own from the pill (no toggle-by-accident).
  const setHeart = useCallback(
    async (msg, add) => {
      if (!threadId || !userId || !canReplyTo(msg)) return;
      const createdAt = Number(msg.createdAt);
      setMessages((prev) =>
        prev.map((m) => (Number(m.createdAt) === createdAt ? withReaction(m, userId, add) : m))
      );
      try {
        const res = await apiFetch(`/api/messages/chat/${add ? "react" : "unreact"}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ threadId, createdAt, reaction: "heart" }),
        });
        if (!res.ok) throw new Error("reaction failed");
        const data = await res.json();
        setMessages((prev) =>
          prev.map((m) => (Number(m.createdAt) === createdAt ? { ...m, reactions: data.reactions } : m))
        );
      } catch {
        setMessages((prev) =>
          prev.map((m) => (Number(m.createdAt) === createdAt ? withReaction(m, userId, !add) : m))
        );
        setError("Не вдалося оновити реакцію");
      }
    },
    [threadId, userId, apiFetch]
  );

  const startReply = (msg) => {
    if (!canReplyTo(msg)) return;
    setEditing(null);
    setReplyTarget(msg);
    closeMenu();
    textareaRef.current?.focus();
  };

  const scrollToMessage = useCallback((createdAt) => {
    const el = listRef.current?.querySelector(`[data-created-at="${createdAt}"]`);
    if (!el) {
      setError("Повідомлення недоступне");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("is-highlighted");
    setTimeout(() => el.classList.remove("is-highlighted"), 1400);
  }, []);

  const openAttachment = useCallback(
    async (msg) => {
      const att = primaryAttachment(msg);
      if (!att || !threadId) return;
      if (att.type === "video") {
        if (att.url) setVideoSrc(att.url);
        return;
      }
      if (att.type === "file") {
        try {
          const res = await apiFetch(
            `/api/messages/chat/attachment?threadId=${encodeURIComponent(threadId)}&createdAt=${encodeURIComponent(msg.createdAt)}`
          );
          if (!res.ok) throw new Error("Не вдалося відкрити файл");
          const data = await res.json();
          window.open(data.url, "_blank", "noopener,noreferrer");
        } catch (e) {
          setError(e.message || "Не вдалося відкрити файл");
        }
      }
    },
    [threadId, apiFetch]
  );

  // one attachment -> optimistic bubble with real progress -> stored message (reconciled by clientId)
  const uploadOne = useCallback(
    (item, replyPreview, existing) =>
      new Promise((resolve) => {
        const clientId = existing?.clientId || makeClientId();
        const att = {
          type: item.kind,
          url: item.kind === "image" ? item.previewUrl : undefined,
          filename: item.file.name,
          size: item.file.size,
          duration: item.duration,
          mimeType: item.file.type,
        };
        const optimistic = existing
          ? { ...existing, status: "uploading", progress: 3 }
          : {
              id: `tmp-${clientId}`,
              clientId,
              senderId: userId,
              createdAt: Date.now(),
              type: item.kind,
              text: "",
              status: "uploading",
              progress: 3,
              attachments: [att],
              replyTo: replyPreview,
              local: item,
              ...(item.kind === "image" ? { mediaUrl: item.previewUrl, previewUrl: item.previewUrl } : {}),
            };
        setMessages((prev) =>
          existing ? patchLocal(prev, clientId, { status: "uploading", progress: 3 }) : mergeMessages(prev, [optimistic])
        );

        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("clientId", clientId);
        if (threadId) fd.append("threadId", threadId);
        else if (peerId) fd.append("to", peerId);
        if (optimistic.replyTo?.createdAt) fd.append("replyToCreatedAt", String(optimistic.replyTo.createdAt));

        getAccessToken()
          .catch(() => null)
          .then((token) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${API_BASE}/api/messages/chat/upload`);
            if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.upload.onprogress = (e) => {
              if (!e.lengthComputable) return;
              const percent = Math.min(99, Math.round((e.loaded / e.total) * 100));
              setMessages((prev) => patchLocal(prev, clientId, { progress: percent }));
            };
            xhr.onload = () => {
              let data = null;
              try {
                data = JSON.parse(xhr.responseText);
              } catch {
                data = null;
              }
              if (xhr.status >= 200 && xhr.status < 300 && data) {
                if (!threadId && data.threadId) {
                  setThreadId(data.threadId);
                  onConversationCreated && onConversationCreated(data.threadId);
                }
                if (data.peer?.id) {
                  setPeer((prev) => prev || { id: data.peer.id });
                  setPeerId((prev) => prev || data.peer.id);
                }
                setMessages((prev) => reconcileSent(prev, clientId, data.message));
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
              } else {
                setError(data?.message || data?.error || "Не вдалося надіслати файл");
                setMessages((prev) => patchLocal(prev, clientId, { status: "failed", progress: 0 }));
              }
              resolve();
            };
            xhr.onerror = () => {
              setError("Немає зʼєднання. Спробуйте ще раз.");
              setMessages((prev) => patchLocal(prev, clientId, { status: "failed", progress: 0 }));
              resolve();
            };
            xhr.send(fd);
          });
      }),
    [threadId, peerId, userId, onConversationCreated]
  );

  const retryMessage = useCallback(
    (m) => {
      if (m.status !== "failed" || !m.local) return;
      uploadOne(m.local, m.replyTo, m);
    },
    [uploadOne]
  );

  const send = useCallback(
    async () => {
      if (disabled) return;
      const value = text.trim();
      if (!editing && !value && !pending.length) return;
      if (editing && !value) {
        setError("Не можна зберегти порожнє повідомлення");
        return;
      }
      setError("");

      if (editing) {
        try {
          const res = await apiFetch(`/api/messages/chat/edit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            threadId,
            createdAt: editing.createdAt,
            text: value,
          }),
          });
          const data = res.ok
            ? await res.json()
            : await res.json().catch(() => ({}));
          if (!res.ok)
            throw new Error(data?.error || "Не вдалося зберегти зміни");
          setMessages((prev) =>
            prev.map((m) =>
              m.createdAt === editing.createdAt
                ? {
                    ...m,
                    text: data.message?.text || value,
                    edited: true,
                    editedAt: data.message?.editedAt || Date.now(),
                  }
                : m
            )
          );
          setEditing(null);
          setText("");
        } catch (e) {
          setError(e.message || "Помилка редагування");
        }
        return;
      }

      const files = pending;
      let replyPreview = replyTarget ? replyPreviewOf(replyTarget) : undefined;
      setPending([]);
      setReplyTarget(null);

      for (const item of files) {
        const useReply = replyPreview;
        replyPreview = undefined;
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(item, useReply);
      }
      if (!value) {
        scrollToEnd(true);
        return;
      }

      const clientId = makeClientId();
      const tempId = `tmp-${clientId}`;
      const optimistic = {
        id: tempId,
        clientId,
        text: value,
        senderId: userId,
        createdAt: Date.now(),
        read: false,
        status: "sending",
        type: "text",
        replyTo: replyPreview,
      };
      setMessages((prev) => mergeMessages(prev, [optimistic]));
      setText("");

      try {
        const body = threadId
        ? { threadId, text: value, clientId, replyToCreatedAt: replyPreview?.createdAt }
        : { to: peerId, text: value, clientId, replyToCreatedAt: replyPreview?.createdAt };
        const res = await apiFetch(`/api/messages/chat/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("Не вдалося надіслати");
        const data = await res.json();

        if (!threadId && data.threadId) {
          setThreadId(data.threadId);
          onConversationCreated && onConversationCreated(data.threadId);
        }
        if (data.peer?.id) {
          setPeer((prev) => prev || { id: data.peer.id });
          setPeerId((prev) => prev || data.peer.id);
        }
        setMessages((prev) => reconcileSent(prev, clientId, data.message));
        scrollToEnd(true);
        syncScrollState();
      } catch (e) {
        setError(e.message || "Помилка відправки");
        setMessages((prev) => patchLocal(prev, clientId, { status: "failed" }));
      }
    },
    [
      disabled,
      pending,
      replyTarget,
      uploadOne,
      text,
      editing,
      threadId,
      peerId,
      userId,
      apiFetch,
      onConversationCreated,
      scrollToEnd,
      syncScrollState,
    ]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isMobile) return;

      if (!e.shiftKey) {
        e.preventDefault();
        send();
      }
    }
  };

  const loadOlder = useCallback(
    async () => {
      if (!threadId || !hasMore || loading) return;
      const first = messages[0];
      if (!first) return;
      try {
        setLoading(true);
        const res = await apiFetch(
  `/api/messages/chat/thread?threadId=${encodeURIComponent(
    threadId
  )}&before=${encodeURIComponent(first.createdAt)}`
);

        if (res.status === 404) {
          setHasMore(false);
          return;
        }

        if (!res.ok) return;
        const data = await res.json();
        const older = Array.isArray(data.messages) ? data.messages : [];
        if (older.length) {
          const prevHeight = listRef.current?.scrollHeight || 0;
          setMessages((prev) => [...older, ...prev]);
          setTimeout(() => {
            const newHeight = listRef.current?.scrollHeight || 0;
            if (listRef.current)
              listRef.current.scrollTop = newHeight - prevHeight;
          }, 0);
        }
        setHasMore(!!data.hasMore);
        if (typeof data.peerLastReadAt === "number")
          setPeerLastReadAt(data.peerLastReadAt);
      } finally {
        setLoading(false);
      }
    },
    [threadId, hasMore, loading, messages, userId, apiFetch]
  );

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        if (el.scrollTop < 24) loadOlder();
        ticking = false;
      });
      ticking = true;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadOlder]);

  const grouped = useMemo(() => {
    const out = [];
    let curDay = null;
    messages.forEach((m) => {
      const d = fmtDay(m.createdAt);
      if (d !== curDay) {
        curDay = d;
        out.push({ type: "day", key: `day_${d}`, label: d });
      }
      out.push({ type: "msg", key: m.id || String(m.createdAt), m });
    });
    return out;
  }, [messages]);

  const headerTitle = peer?.username || "Чат";
  const headerAvatar = peer?.avatar || DEFAULT_AVATAR;

  const canEdit = (m) =>
    m.senderId === userId &&
    m.type === "text" &&
    Date.now() - Number(m.createdAt) <= EDIT_WINDOW_MS;

  const handleCopy = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.text || "");
    } catch {}
  };

  const onClickClip = () => {
    if (disabled) return;
    setAttachMenuOpen((v) => !v);
  };

  // picked files wait in the tray (with a remove button) until Send
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setAttachMenuOpen(false);
    if (!files.length || disabled) return;

    const accepted = [];
    for (const file of files.slice(0, 6)) {
      const problem = validateUploadFile(file);
      if (problem) {
        setError(problem);
        continue;
      }
      const kind = kindOfFile(file);
      let duration;
      if (kind === "video") {
        // eslint-disable-next-line no-await-in-loop
        duration = await readVideoDuration(file);
        const tooLong = validateVideoDuration(duration);
        if (tooLong) {
          setError(tooLong);
          continue;
        }
      }
      accepted.push({
        file,
        kind,
        duration: duration || undefined,
        previewUrl: kind === "image" ? URL.createObjectURL(file) : undefined,
      });
    }
    if (accepted.length) {
      setError("");
      setPending((prev) => [...prev, ...accepted].slice(0, 6));
    }
  };

  const removePending = (index) => {
    setPending((prev) => {
      const item = prev[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const headerClickToProfile = () => {
    if (peer?.id) navigate(`/profile/${peer.id}`);
  };

  const photos = useMemo(
    () =>
      messages
        .filter((m) => m.type === "image" && (m.previewUrl || m.mediaUrl))
        // the viewer shows the FULL image; previewUrl is a small thumbnail
        .map((m) => ({
          src: m.mediaUrl || m.previewUrl,
        })),
    [messages]
  );

  const handleOpenImage = useCallback(
    (src) => {
      if (!src) return;
      setViewerSrc(src);
      if (!photos.length) {
        setViewerIndex(0);
        return;
      }
      const idx = photos.findIndex((p) => p.src === src);
      setViewerIndex(idx >= 0 ? idx : 0);
    },
    [photos]
  );

  const onClickScrollDown = () => {
    scrollToEnd(true);
    syncScrollState();
  };

  return (
    <div
      className="chat"
      onClick={() => ctx.open && closeMenu()}
      style={{ "--chat-composer-h": `${composerH}px` }}
    >
      {/* header */}
<div className="chat__header">
  <div className="chat__header-left">
    <button
      className="chat__icon"
      onClick={onBack}
      aria-label="Назад"
      title="Назад"
      type="button"
    >
      <img src={backIcon} alt="Назад" />
    </button>
  </div>

  <div
    className={`chat__header-center ${peer?.id ? "is-clickable" : ""}`}
    onClick={headerClickToProfile}
    role={peer?.id ? "button" : undefined}
    tabIndex={peer?.id ? 0 : undefined}
  >
    <div className="chat__avatar chat__avatar--sm">
      <img
        src={headerAvatar}
        alt={headerTitle}
        onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
      />
    </div>

    <div className="chat__center-meta">
      <div className="chat__name chat__name--center">{headerTitle}</div>
      {isPeerTyping && (
        <div className="chat__status chat__status--center">
          Набирає повідомлення…
        </div>
      )}
    </div>
  </div>

  <div className="chat__header-right">
    <button
      className="chat__icon"
      title="Storage"
      onClick={() => setStorageOpen(true)}
      type="button"
    >
      <img src={storageIcon} alt="Storage" />
    </button>
  </div>
</div>


      {/* messages list */}
      <div className="chat__list" ref={listRef}>
        {hasMore && (
          <div className="chat__load-older" onClick={loadOlder}>
            {loading ? "Завантаження…" : "Показати попередні"}
          </div>
        )}

        {useMemo(
          () =>
            grouped.map((row) =>
              row.type === "day" ? (
                <div className="chat__day" key={row.key}>
                  <span>{row.label}</span>
                </div>
              ) : (
                <MessageBubble
                  key={row.key}
                  m={row.m}
                  isOwn={row.m.senderId === userId}
                  isRead={
                    row.m.senderId === userId &&
                    row.m.createdAt <= peerLastReadAt
                  }
                  onContextMenu={(e) => {
                    e.preventDefault();
                    openMenuAt(e.clientX, e.clientY, row.m);
                  }}
                  onLongPress={(pos) => openMenuAt(pos.x, pos.y, row.m)}
                  onOpenImage={handleOpenImage}
                  onImageLoad={handleImageLoad}
                  userId={userId}
                  peerName={peer?.username}
                  onOpenAttachment={openAttachment}
                  onReplyQuoteClick={scrollToMessage}
                  onRemoveMyHeart={(m) => setHeart(m, false)}
                  onRetry={retryMessage}
                />
              )
            ),
          [grouped, userId, peer?.username, peerLastReadAt, handleOpenImage, handleImageLoad, openAttachment, scrollToMessage, setHeart, retryMessage]
        )}

        <div ref={endRef} />
      </div>

      {showScrollDown && (
        <button
          className="chat__scroll-down"
          onClick={onClickScrollDown}
          aria-label="До низу"
          title="До низу"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M12 16.2c-.3 0-.6-.12-.83-.35l-6-6a1.18 1.18 0 0 1 0-1.66c.46-.46 1.2-.46 1.66 0L12 13.34l5.17-5.15c.46-.46 1.2-.46 1.66 0a1.18 1.18 0 0 1 0 1.66l-6 6c-.23.23-.53.35-.83.35Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}

      {error && <div className="chat__error">{error}</div>}

      {(replyTarget || pending.length > 0) && (
        <div className="chat__above-composer">
          {replyTarget && (
            <div className="chat-reply-bar">
              <span className="chat-reply-bar__icon" aria-hidden="true">↩</span>
              {replyPreviewOf(replyTarget).thumbnailUrl && (
                <img className="chat-reply-bar__thumb" src={replyPreviewOf(replyTarget).thumbnailUrl} alt="" />
              )}
              <div className="chat-reply-bar__text">
                <div className="chat-reply-bar__author">
                  {replyTarget.senderId === userId ? "Ви" : peer?.username || "Користувач"}
                </div>
                <div className="chat-reply-bar__body">{replyBody(replyPreviewOf(replyTarget))}</div>
              </div>
              <button type="button" className="chat-reply-bar__close" onClick={() => setReplyTarget(null)} aria-label="Скасувати відповідь">
                ×
              </button>
            </div>
          )}

          {pending.length > 0 && (
            <div className="chat-pending">
              {pending.map((item, i) => (
                <div className="chat-pending__item" key={`${item.file.name}-${i}`}>
                  {item.previewUrl ? (
                    <img src={item.previewUrl} alt="" />
                  ) : (
                    <div className="chat-pending__file">
                      <span>{item.kind === "video" ? "🎞" : "📄"}</span>
                      <span className="chat-pending__name">{item.file.name}</span>
                      <span className="chat-pending__meta">
                        {item.kind === "video" && item.duration ? formatDuration(item.duration) : formatFileSize(item.file.size)}
                      </span>
                    </div>
                  )}
                  <button type="button" onClick={() => removePending(i)} aria-label="Прибрати вкладення">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* composer */}
      <div
        className={`chat__composer ${disabled ? "is-disabled" : ""}`}
        ref={composerRef}
      >
        {editing && (
          <div className="edit-banner">
            Редагуєте повідомлення
            <button className="edit-cancel" onClick={cancelEdit}>
              Скасувати
            </button>
          </div>
        )}

        <div className="chat__attach-wrap">
          <button
            className="chat__attach"
            title="Додати"
            aria-label="Додати вкладення"
            aria-expanded={attachMenuOpen}
            onClick={onClickClip}
            type="button"
          >
            <img src={clipIcon} alt="" />
          </button>

          {attachMenuOpen && (
            <div className="chat-attach-menu" role="menu">
              {isMobile && (
                <button type="button" role="menuitem" onClick={() => cameraInputRef.current?.click()}>
                  📷 Зробити фото
                </button>
              )}
              <button type="button" role="menuitem" onClick={() => mediaInputRef.current?.click()}>
                🖼 Фото або відео
              </button>
              <button type="button" role="menuitem" onClick={() => docInputRef.current?.click()}>
                📎 Файл
              </button>
            </div>
          )}

          <input type="file" ref={mediaInputRef} accept={MEDIA_ACCEPT} multiple hidden onChange={onPickFiles} />
          <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" hidden onChange={onPickFiles} />
          <input type="file" ref={docInputRef} accept={FILE_ACCEPT} hidden onChange={onPickFiles} />
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={onChangeText}
          onKeyDown={onKeyDown}
          placeholder={
            disabled
              ? "Повідомлення недоступні"
              : editing
              ? isMobile
                ? "Змініть текст і натисніть кнопку відправки"
                : "Змініть текст і натисніть Enter"
              : "Напишіть повідомлення…"
          }
          disabled={disabled}
          rows={1}
        />
        <div className="chat__composer-actions">
          <button
            className="chat__send"
            onClick={send}
            disabled={disabled || (!text.trim() && !pending.length)}
            aria-label="Надіслати"
            title="Надіслати"
          >
            <img src={sendIcon} alt="" />
          </button>
        </div>
      </div>

      <ChatActions
        open={ctx.open}
        x={ctx.x}
        y={ctx.y}
        msg={ctx.msg}
        isOwn={ctx.msg?.senderId === userId}
        canEdit={ctx.msg ? canEdit(ctx.msg) : false}
        onEdit={startEdit}
        onCopy={handleCopy}
        onUnsend={doUnsend}
        onClose={closeMenu}
        canReply={ctx.msg ? canReplyTo(ctx.msg) : false}
        onReply={startReply}
        canReact={ctx.msg ? canReplyTo(ctx.msg) : false}
        hasHeart={ctx.msg ? hasReaction(ctx.msg, userId) : false}
        onToggleHeart={(m, add) => {
          closeMenu();
          setHeart(m, add);
        }}
      />

      {videoSrc && (
        <div className="chat-video-viewer" onClick={() => setVideoSrc(null)} role="dialog" aria-label="Відео">
          <button type="button" className="chat-video-viewer__close" onClick={() => setVideoSrc(null)} aria-label="Закрити">
            ×
          </button>
          <video src={videoSrc} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {viewerSrc && (
        <PhotoDetails
          open={!!viewerSrc}
          photos={photos}
          startIndex={viewerIndex}
          onClose={() => setViewerSrc(null)}
        />
      )}

      {storageOpen && threadId && (
        <ChatStorage threadId={threadId} onClose={() => setStorageOpen(false)} />
      )}
    </div>
  );
};

/* bubble */
const MessageBubble = ({
  m,
  isOwn,
  isRead,
  onContextMenu,
  onLongPress,
  onOpenImage,
  onImageLoad,
  userId,
  peerName,
  onOpenAttachment,
  onReplyQuoteClick,
  onRemoveMyHeart,
  onRetry,
}) => {
  const touchRef = useRef({
    timer: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
  });

  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const x = t.clientX,
      y = t.clientY;
    touchRef.current.startX = x;
    touchRef.current.startY = y;
    touchRef.current.lastX = x;
    touchRef.current.lastY = y;
    clearTimeout(touchRef.current.timer);
    touchRef.current.timer = setTimeout(() => {
      onLongPress &&
        onLongPress({
          x: touchRef.current.lastX,
          y: touchRef.current.lastY,
        });
    }, 500);
  };
  const onTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const x = t.clientX,
      y = t.clientY;
    touchRef.current.lastX = x;
    touchRef.current.lastY = y;
    const dx = Math.abs(x - touchRef.current.startX);
    const dy = Math.abs(y - touchRef.current.startY);
    if (dx > 10 || dy > 10) clearTimeout(touchRef.current.timer);
  };
  const cancelTouch = () => clearTimeout(touchRef.current.timer);

  const cls = [
    "bubble",
    isOwn ? "bubble--own" : "bubble--peer",
    m.status === "failed" ? "is-failed" : "",
    m.status === "sending" || m.status === "uploading" ? "is-sending" : "",
    m.type === "unsent" ? "is-unsent" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isImage = m.type === "image" && (m.previewUrl || m.mediaUrl);
  const attachment = primaryAttachment(m);
  const isVideo = m.type === "video" && attachment?.type === "video";
  const isFile = m.type === "file" && attachment?.type === "file";
  const heart = (m.reactions || []).find((r) => r.reaction === "heart" && r.count > 0);
  const heartMine = !!heart && !!userId && heart.userIds.includes(userId);

  const linkify = (s) => {
    const urlRe = /(https?:\/\/[^\s<>"']+)/gi;
    const parts = String(s || "").split(urlRe);
    return parts.map((part, i) =>
      /^https?:\/\/[^\s<>"']+$/i.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  const cvantoMeta = useMemo(() => extractCvantoLink(m.text), [m.text]);
  const showPreview = !!cvantoMeta && !isImage && !isVideo && !isFile && m.type !== "unsent";

  const quote =
    m.replyTo && m.type !== "unsent" ? (
      <button
        type="button"
        className={`reply-quote ${m.replyTo.unavailable ? "is-unavailable" : ""}`}
        disabled={!!m.replyTo.unavailable}
        onClick={() => onReplyQuoteClick && onReplyQuoteClick(Number(m.replyTo.createdAt))}
      >
        {m.replyTo.thumbnailUrl && !m.replyTo.unavailable && (
          <img className="reply-quote__thumb" src={m.replyTo.thumbnailUrl} alt="" />
        )}
        <span className="reply-quote__text">
          {!m.replyTo.unavailable && (
            <span className="reply-quote__author">
              {m.replyTo.senderId === userId ? "Ви" : peerName || "Користувач"}
            </span>
          )}
          <span className="reply-quote__body">{replyBody(m.replyTo)}</span>
        </span>
      </button>
    ) : null;

  const content =
    m.type === "unsent" ? (
      <em className="unsent">Повідомлення видалено</em>
    ) : isVideo ? (
      <button
        type="button"
        className="bubble__video"
        onClick={() => (m.status === "failed" ? onRetry && onRetry(m) : m.status !== "uploading" && onOpenAttachment && onOpenAttachment(m))}
        aria-label="Відео"
      >
        {attachment.thumbnailUrl ? <img src={attachment.thumbnailUrl} alt="" /> : <span className="bubble__video-fallback" />}
        <span className="bubble__video-play" aria-hidden="true">{m.status === "failed" ? "↻" : "▶"}</span>
        {!!attachment.duration && <span className="bubble__video-duration">{formatDuration(attachment.duration)}</span>}
        {m.status === "uploading" && <span className="bubble__video-state">{m.progress ? `${m.progress}%` : "…"}</span>}
        {attachment.status === "processing" && m.status !== "uploading" && (
          <span className="bubble__video-state">Оптимізація…</span>
        )}
      </button>
    ) : isFile ? (
      <button
        type="button"
        className="bubble__file"
        onClick={() => (m.status === "failed" ? onRetry && onRetry(m) : m.status !== "uploading" && onOpenAttachment && onOpenAttachment(m))}
      >
        <span className="bubble__file-icon" aria-hidden="true">📄</span>
        <span className="bubble__file-text">
          <span className="bubble__file-name">{attachment.filename || "Файл"}</span>
          <span className="bubble__file-meta">
            {m.status === "failed"
              ? "Не вдалося. Натисніть, щоб повторити"
              : m.status === "uploading"
              ? m.progress
                ? `${m.progress}%`
                : "Завантаження…"
              : formatFileSize(attachment.size)}
          </span>
        </span>
      </button>
    ) : isImage ? (
      <div className="bubble__imgwrap">
        <img
          className="bubble__img"
          src={m.previewUrl || m.mediaUrl}
          alt=""
          onLoad={onImageLoad}
          onClick={() => onOpenImage && onOpenImage(m.mediaUrl || m.previewUrl)}
        />
      </div>
    ) : (
      <div className="bubble__text">{linkify(m.text)}</div>
    );

  const ticks = isOwn ? (
    <span className="bubble__ticks">
      {m.status === "sending" || m.status === "uploading" ? (
        <span className="dot-sending" aria-label="Надсилається" />
      ) : (
        <img
          src={isRead ? readedIcon : deliveredIcon}
          alt={isRead ? "Прочитано" : "Доставлено"}
          draggable="false"
        />
      )}
    </span>
  ) : null;

  return (
    <div
      className={cls}
      data-created-at={m.createdAt}
      onContextMenu={onContextMenu}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={cancelTouch}
      onTouchCancel={cancelTouch}
    >
      {quote}
      {content}
      {showPreview && <LinkPreview meta={cvantoMeta} />}
      <div className="bubble__meta">
        <span className="bubble__time">
          {fmtTime(m.createdAt)}
          {m.edited && m.type === "text" ? " • змінено" : ""}
        </span>
        {ticks}
      </div>
      {heart && (
        <button
          type="button"
          className={`bubble__reaction ${heartMine ? "is-mine" : ""}`}
          disabled={!heartMine}
          onClick={() => heartMine && onRemoveMyHeart && onRemoveMyHeart(m)}
          title={heartMine ? "Прибрати вашу реакцію" : "Реакція"}
        >
          ❤️{heart.count > 1 ? <span>{heart.count}</span> : null}
        </button>
      )}
    </div>
  );
};

export default Chat;
